import React from 'react';
import PropTypes from 'prop-types';

import {Alert, Box, Card, CircularProgress, Dropdown, DropdownMenuItem, Stack, Typography} from 'views/components';

import {Translate, translate} from '../Translate';

import Pagination from '@mui/material/Pagination';

const CardTable = (props) => {
  const {
    data,
    card: CardRender,
    isLoading,
    loadMore,
    hasMore,
    emptyState,
  } = props;

  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [page, setPage] = React.useState(1);

  React.useEffect(() => {
    const endRecord = page * rowsPerPage;

    if (endRecord >= data.length && hasMore && !isLoading) {
      loadMore();
    }
  }, [page, rowsPerPage, data, hasMore, isLoading, loadMore]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (value) => {
    setRowsPerPage(parseInt(value, 10));
    setPage(1); // Reset to first page on rows per page change
  };

  const startIndex = (page - 1) * rowsPerPage;
  const endIndex = Math.min(startIndex + rowsPerPage, data.length);
  const paginatedData = data.slice(startIndex, endIndex);


  return (
    <Stack
      spacing={2}
      sx={{
        width: '100%',
      }}
    >
      {
        paginatedData.map((e, index) => (
          <Card
            key={index}
            sx={{
              width: '100%',
            }}
          >
            <CardRender
              item={e}
            />
          </Card>
        ))
      }
      {
        isLoading &&
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress />
        </Box>
      }
      {
        (!isLoading && !data.length) &&
        (
          emptyState || (
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Alert severity="info" sx={{mt: 2}}>
                <Translate expression='table.NoResult'/>
              </Alert>
            </Box>
          )
        )
      }
      {
        Boolean(data.length) &&
        <Stack
          alignItems='center'
          justifyContent="space-between"
          direction='row'
          sx={{
            width: '100%',
            display: 'flex',
          }}
        >
          <Typography variant="body2">
            <Translate
              expression={hasMore ? 'table.LabelDisplayedRowsMore' : 'table.LabelDisplayedRowsNoMore'}
              substitute={{from: startIndex + 1, to: endIndex, numOfRows: data.length}}
            />
          </Typography>
          <Pagination
            count={Math.ceil(data.length / rowsPerPage)}
            page={page}
            onChange={handleChangePage}
            showFirstButton
            showLastButton
          />
          <Box
            sx={{
              width: '100px',
            }}
          >
            <Dropdown
              value={rowsPerPage}
              onChange={handleRowsPerPageChange}
              label={translate('table.RowsPerPage')}
              variant={'standard'}
            >
              <DropdownMenuItem value={5}>
                5
              </DropdownMenuItem>
              <DropdownMenuItem value={10}>
                10
              </DropdownMenuItem>
              <DropdownMenuItem value={20}>
                20
              </DropdownMenuItem>
              <DropdownMenuItem value={50}>
                50
              </DropdownMenuItem>
            </Dropdown>
          </Box>
        </Stack>
      }
    </Stack>
  );
};

CardTable.propTypes = {
  data: PropTypes.array.isRequired,
  card: PropTypes.object.isRequired,
  isLoading: PropTypes.bool,
  loadMore: PropTypes.func,
  hasMore: PropTypes.bool,
  emptyState: PropTypes.node,
};

export default React.memo(CardTable);
